import { createContext, useContext, useEffect, useState, useRef, useCallback, ReactNode } from 'react'
import { supabase, isSupabaseConfigured } from '../lib/supabase'
import { logger } from '../lib/productionLogger'
import { useTabVisibility } from '../hooks/useTabVisibility'
import type { User, Session } from '@supabase/supabase-js'

type AuthState = 'loading' | 'signedOut' | 'signedIn' | 'error'

interface AuthContextValue {
  authState: AuthState
  user: User | null
  session: Session | null
  error: string | null
  signUp: (email: string, password: string, metadata?: any) => Promise<any>
  signIn: (email: string, password: string) => Promise<any>
  signOut: () => Promise<void>
  retry: () => void
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [authState, setAuthState] = useState<AuthState>('loading')
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [error, setError] = useState<string | null>(null)
  const mountedRef = useRef(true)
  const initDoneRef = useRef(false)

  // Initialize auth once
  const initAuth = useCallback(async () => {
    if (!isSupabaseConfigured) {
      logger.error('[AuthProvider] Supabase not configured')
      setAuthState('error')
      setError('Supabase configuration is missing')
      return
    }

    try {
      logger.log('[AuthProvider] Initializing auth...')
      const { data, error: sessionError } = await supabase.auth.getSession()

      if (sessionError) {
        throw sessionError
      }

      if (!mountedRef.current) return

      if (data.session?.user) {
        logger.log('[AuthProvider] Session found', { userId: data.session.user.id })
        setUser(data.session.user)
        setSession(data.session)
        setAuthState('signedIn')
      } else {
        logger.log('[AuthProvider] No session found')
        setUser(null)
        setSession(null)
        setAuthState('signedOut')
      }
      setError(null)
    } catch (e) {
      logger.error('[AuthProvider] Init error:', e)
      if (!mountedRef.current) return
      setAuthState('error')
      setError(e instanceof Error ? e.message : 'Authentication failed')
    } finally {
      initDoneRef.current = true
    }
  }, [])

  // Setup auth subscription
  useEffect(() => {
    mountedRef.current = true

    // Run init once
    if (!initDoneRef.current) {
      initAuth()
    }

    // Subscribe to auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      logger.log('[AuthProvider] Auth state change:', event)

      if (!mountedRef.current) return

      if (event === 'SIGNED_OUT') {
        setUser(null)
        setSession(null)
        setAuthState('signedOut')
        setError(null)
      } else if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        if (session?.user) {
          setUser(session.user)
          setSession(session)
          setAuthState('signedIn')
          setError(null)
        }
      } else if (event === 'USER_UPDATED') {
        if (session?.user) {
          setUser(session.user)
          setSession(session)
        }
      }
    })

    return () => {
      mountedRef.current = false
      subscription.unsubscribe()
    }
  }, [initAuth])

  const signUp = useCallback(async (email: string, password: string, metadata?: any) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: metadata || {}
        }
      })
      if (error) throw error
      return data
    } catch (e) {
      logger.error('[AuthProvider] Sign up error:', e)
      throw e
    }
  }, [])

  const signIn = useCallback(async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      })
      if (error) throw error
      return data
    } catch (e) {
      logger.error('[AuthProvider] Sign in error:', e)
      throw e
    }
  }, [])

  const signOut = useCallback(async () => {
    try {
      const { error } = await supabase.auth.signOut()
      if (error) throw error
    } catch (e) {
      logger.error('[AuthProvider] Sign out error:', e)
      throw e
    }
  }, [])

  const retry = useCallback(() => {
    logger.log('[AuthProvider] Retrying auth initialization')
    setAuthState('loading')
    setError(null)
    initDoneRef.current = false
    initAuth()
  }, [initAuth])

  // Handle tab visibility for session refresh
  useTabVisibility({
    refreshSessionOnVisible: true,
    onVisible: () => {
      logger.log('[AuthProvider] Tab visible, session will be checked');
    },
  });

  return (
    <AuthContext.Provider
      value={{
        authState,
        user,
        session,
        error,
        signUp,
        signIn,
        signOut,
        retry
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
